import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft, DollarSign, TrendingUp, MousePointer,
  CheckCircle, Clock, ExternalLink, Percent, BarChart3
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import Logo from '@/components/Logo';
import AffiliateChart from '@/components/AffiliateChart';
import { getProductImage } from '@/utils/imageUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function AffiliatePayouts() {
  const navigate = useNavigate();
  const [payouts, setPayouts] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/');
      return;
    }
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const token = localStorage.getItem('token');
      const [payoutsRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/api/affiliate/payouts/history`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/api/affiliate/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setPayouts(payoutsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error('Failed to load affiliate data');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-[#00F0FF]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      {/* Header */}
      <header className="border-b border-white/10 backdrop-blur-xl bg-black/40 sticky top-0 z-50">
        <div className="container mx-auto px-6 md:px-12 py-4">
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              onClick={() => navigate('/profile')}
              className="text-[#00F0FF] hover:text-[#00C2CC]"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back to Profile
            </Button>
            <Logo />
          </div>
        </div>
      </header>

      <main className="container mx-auto px-6 md:px-12 py-12">
        <div className="flex items-center gap-3 mb-8">
          <DollarSign className="w-8 h-8 text-[#00F0FF]" />
          <h1 className="text-3xl font-bold">Affiliate Earnings</h1>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-neutral-900/50 border border-white/10 rounded-xl p-4">
            <DollarSign className="w-5 h-5 text-[#00F0FF] mb-2" />
            <p className="text-2xl font-bold">${payouts?.total_earnings?.toFixed(2) || '0.00'}</p>
            <p className="text-xs text-neutral-400">Total Earnings</p>
          </div>
          <div className="bg-neutral-900/50 border border-white/10 rounded-xl p-4">
            <MousePointer className="w-5 h-5 text-white mb-2" />
            <p className="text-2xl font-bold">{stats?.total_clicks || 0}</p>
            <p className="text-xs text-neutral-400">Total Clicks</p>
          </div>
          <div className="bg-neutral-900/50 border border-white/10 rounded-xl p-4">
            <TrendingUp className="w-5 h-5 text-[#00F0FF] mb-2" />
            <p className="text-2xl font-bold">{stats?.total_conversions || 0}</p>
            <p className="text-xs text-neutral-400">Conversions</p>
          </div>
          <div className="bg-neutral-900/50 border border-white/10 rounded-xl p-4">
            <Percent className="w-5 h-5 text-[#10B981] mb-2" />
            <p className="text-2xl font-bold">{stats?.conversion_rate?.toFixed(1) || 0}%</p>
            <p className="text-xs text-neutral-400">Conv. Rate</p>
          </div>
        </div>

        {/* Performance Charts */}
        {stats?.chart_data && stats.chart_data.length > 0 && (
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-neutral-900/50 border border-white/10 rounded-xl p-6">
              <h3 className="font-bold mb-4 flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-[#00F0FF]" />
                Clicks (Last 7 Days)
              </h3>
              <AffiliateChart
                data={stats.chart_data.map(d => ({
                  label: d.label,
                  value: d.clicks,
                  color: '#00F0FF'
                }))}
                type="bar"
              />
            </div>
            <div className="bg-neutral-900/50 border border-white/10 rounded-xl p-6">
              <h3 className="font-bold mb-4 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-[#00F0FF]" />
                Conversions (Last 7 Days)
              </h3>
              <AffiliateChart
                data={stats.chart_data.map(d => ({
                  label: d.label,
                  value: d.conversions,
                  color: '#00F0FF'
                }))}
                type="bar"
              />
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-4 mb-6">
          <button
            onClick={() => setActiveTab('overview')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === 'overview'
                ? 'bg-[#00F0FF] text-black'
                : 'bg-neutral-800 text-neutral-300 hover:bg-neutral-700'
            }`}
          >
            Payout History
          </button>
          <button
            onClick={() => setActiveTab('products')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === 'products'
                ? 'bg-[#00F0FF] text-black'
                : 'bg-neutral-800 text-neutral-300 hover:bg-neutral-700'
            }`}
          >
            Products Promoted
          </button>
        </div>

        {/* Content */}
        {activeTab === 'overview' ? (
          <div className="bg-neutral-900/50 border border-white/10 rounded-2xl overflow-hidden">
            {payouts?.payouts?.length === 0 ? (
              <div className="text-center py-16">
                <DollarSign className="w-12 h-12 text-neutral-700 mx-auto mb-4" />
                <h3 className="text-lg font-bold mb-2">No payouts yet</h3>
                <p className="text-neutral-400">Start promoting products to earn commissions!</p>
              </div>
            ) : (
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left p-4 text-neutral-400 font-medium">Date</th>
                    <th className="text-left p-4 text-neutral-400 font-medium">Product</th>
                    <th className="text-right p-4 text-neutral-400 font-medium">Sale Amount</th>
                    <th className="text-right p-4 text-neutral-400 font-medium">Commission</th>
                    <th className="text-right p-4 text-neutral-400 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {payouts?.payouts?.map((payout, index) => (
                    <tr key={index} className="border-b border-white/5 hover:bg-white/5">
                      <td className="p-4 text-sm">{formatDate(payout.created_at)}</td>
                      <td className="p-4">
                        <span className="font-medium">{payout.product_name}</span>
                        <span className="text-xs text-neutral-400 ml-2">({payout.commission_rate}%)</span>
                      </td>
                      <td className="p-4 text-right">${payout.sale_amount.toFixed(2)}</td>
                      <td className="p-4 text-right font-bold text-[#00F0FF]">
                        ${payout.commission.toFixed(2)}
                      </td>
                      <td className="p-4 text-right">
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-500/20 text-green-400 text-xs rounded-full">
                          <CheckCircle className="w-3 h-3" />
                          Paid
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {stats?.products_promoted?.length === 0 ? (
              <div className="col-span-full text-center py-16 bg-neutral-900/50 border border-white/10 rounded-2xl">
                <h3 className="text-lg font-bold mb-2">No products promoted yet</h3>
                <p className="text-neutral-400 mb-4">Browse products and join affiliate programs</p>
                <Button
                  onClick={() => navigate('/')}
                  className="bg-[#00F0FF] hover:bg-[#00C2CC] text-black"
                >
                  Browse Products
                </Button>
              </div>
            ) : (
              stats?.products_promoted?.map((item) => (
                <div
                  key={item.link_code}
                  className="bg-neutral-900/50 border border-white/10 rounded-xl p-4"
                >
                  <div className="flex items-start gap-3 mb-4">
                      <img
                        src={getProductImage(item.product)}
                        alt={item.product?.name || 'Product'}
                        className="w-16 h-16 rounded-lg object-cover"
                        onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1633356122544-f134324a6cee?w=400&h=400&fit=crop&auto=format'; }}
                      />
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold truncate">{item.product?.name}</h3>
                      <p className="text-sm text-[#00F0FF]">{item.commission_rate}% commission</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-center text-sm">
                    <div>
                      <p className="font-bold">{item.clicks}</p>
                      <p className="text-xs text-neutral-400">Clicks</p>
                    </div>
                    <div>
                      <p className="font-bold">{item.conversions}</p>
                      <p className="text-xs text-neutral-400">Sales</p>
                    </div>
                    <div>
                      <p className="font-bold text-[#00F0FF]">${item.earnings.toFixed(2)}</p>
                      <p className="text-xs text-neutral-400">Earned</p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </main>
    </div>
  );
}

export default AffiliatePayouts;
